import {useCallback, useEffect, useRef, useState} from "react";

import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {NetworkRunTestResult} from "@/lib/types/network.ts";

export type RunState = "idle" | "running" | "completed" | "failed";

// Backend field-validation errors: {field name -> messages}.
export type RunTestFieldErrors = Record<string, string[]>;

const POLL_INTERVAL_MS = 2000;
const RUN_TIMEOUT_MS = 90000;
const GENERIC_ERROR = "Probe server responded with an error.";

// The run-test endpoint is a legacy Django ajax view (@csrf_exempt), so a
// credentialed cross-origin fetch with JSON headers is all it needs — mirroring
// the other run-test fetches (e.g. checks/rta/Traceroute).
const FETCH_OPTS: RequestInit = {
  credentials: "include",
  mode: "cors",
  headers: {"Content-Type": "application/json"},
};

interface RunTestStartResponse {
  success: boolean;
  error?: string;
  fields?: RunTestFieldErrors;
  data: {task_id: string | null; status: string; result?: NetworkRunTestResult; error?: string};
}

interface RunTestPollResponse {
  success: boolean;
  data: {status: string; result?: NetworkRunTestResult; error?: string};
}

export const useNetworkRunTest = ({
  runTestURL,
  onFieldErrors,
}: {
  runTestURL: string;
  onFieldErrors?: (fields: RunTestFieldErrors) => void;
}) => {
  const [state, setState] = useState<RunState>("idle");
  const [result, setResult] = useState<NetworkRunTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const elapsedRef = useRef(0);
  // The pending poll timer plus a flag set on unmount: together they stop the
  // recursive poll loop from firing more GETs — or calling setState — after the
  // component that owns this hook has been navigated away from mid-run.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  // Kept in a ref so `run` stays stable while always calling the latest handler.
  const onFieldErrorsRef = useRef(onFieldErrors);
  onFieldErrorsRef.current = onFieldErrors;

  useEffect(() => {
    // Re-arm on mount so a genuine remount — or React StrictMode's dev-only
    // mount→unmount→mount cycle — doesn't leave the flag stuck `true` (which
    // would make every poll/finish/fail below a no-op and kill polling).
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const url = `${URL_ORIGINAL_WEBSITE}${runTestURL}`;

  const fail = useCallback((message: string) => {
    if (cancelledRef.current) return;
    setState("failed");
    setError(message);
  }, []);

  const finish = useCallback((status: string, data: NetworkRunTestResult | undefined, errorMessage?: string) => {
    if (cancelledRef.current) return;
    setResult(data ?? null);
    // A check that ran but reported failing locations still returns a full
    // structured result (probes with FAIL status); the backend marks that task
    // FAILED and echoes the output into `error`, but it is NOT a run-test error —
    // render the probe list. Only surface an error when there is no structured
    // result to show (e.g. a 90s timeout / no probes), which would otherwise
    // leave the panel blank.
    const hasProbes = !!data && data.probes.length > 0;
    if (status === "COMPLETED" || hasProbes) {
      setState("completed");
    } else {
      setState("failed");
      setError(errorMessage || GENERIC_ERROR);
    }
  }, []);

  const poll = useCallback(
    async (taskId: string) => {
      if (cancelledRef.current) return;
      if (elapsedRef.current >= RUN_TIMEOUT_MS) {
        fail("Timeout awaiting response from monitoring server.");
        return;
      }
      try {
        const res = await fetch(`${url}?task_id=${encodeURIComponent(taskId)}`, {...FETCH_OPTS, method: "GET"});
        if (cancelledRef.current) return;
        if (!res.ok) {
          fail(GENERIC_ERROR);
          return;
        }
        const body: RunTestPollResponse = await res.json();
        if (cancelledRef.current) return;
        if (!body.success) {
          fail(GENERIC_ERROR);
          return;
        }
        const {status, result: data, error: taskError} = body.data;
        if (status === "NEW" || status === "RUNNING") {
          elapsedRef.current += POLL_INTERVAL_MS;
          timeoutRef.current = setTimeout(() => poll(taskId), POLL_INTERVAL_MS);
          return;
        }
        finish(status, data, taskError);
      } catch {
        fail(GENERIC_ERROR);
      }
    },
    [url, fail, finish]
  );

  const run = useCallback(
    async (payload: Record<string, unknown>) => {
      setState("running");
      setResult(null);
      setError(null);
      elapsedRef.current = 0;
      try {
        const res = await fetch(url, {...FETCH_OPTS, method: "POST", body: JSON.stringify(payload)});
        if (!res.ok) {
          fail(GENERIC_ERROR);
          return;
        }
        const body: RunTestStartResponse = await res.json();
        if (!body.success) {
          // Field-level validation errors (e.g. Locations missing) belong on the
          // form, not in this panel: hand them off and return to idle.
          if (body.fields && Object.keys(body.fields).length > 0) {
            onFieldErrorsRef.current?.(body.fields);
            setState("idle");
            return;
          }
          fail(body.error || "Run test failed to start.");
          return;
        }
        if (!body.data.task_id) {
          finish(body.data.status, body.data.result, body.data.error);
          return;
        }
        poll(body.data.task_id);
      } catch {
        fail(GENERIC_ERROR);
      }
    },
    [url, fail, poll, finish]
  );

  return {state, result, error, run};
};
